/**
 * useRbacMatrix (US-AE-IMP · @UC9002) — nạp ma trận RBAC-UI dùng chung (nav visibility + action).
 *
 * Fetch 1 lần (staleTime dài — matrix ít đổi), fallback DEFAULT_ADMIN_RBAC_UI_MATRIX khi đang tải/lỗi
 * để nav LUÔN render được (không chớp trắng, không kẹt). Server @Roles vẫn chốt thật; matrix chỉ UX.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_ADMIN_RBAC_UI_MATRIX,
  canViewNav,
  canActNav,
  type AdminRbacUiMatrix,
  type AdminRole,
} from "@goppy/contracts";
import { getCurrentAdmin } from "@/lib/auth.js";
import { fetchRbacMatrix, saveRbacMatrix } from "./api.js";

const RBAC_MATRIX_KEY = ["admin", "rbac-ui-matrix"] as const;

/** Trả matrix hiệu lực (fallback DEFAULT khi chưa có dữ liệu). */
export function useRbacMatrix(): AdminRbacUiMatrix {
  const { data } = useQuery({
    queryKey: RBAC_MATRIX_KEY,
    queryFn: fetchRbacMatrix,
    staleTime: 5 * 60_000, // 5' — matrix đổi hiếm
    retry: 1,
  });
  return data ?? DEFAULT_ADMIN_RBAC_UI_MATRIX;
}

/** Query đầy đủ (cho trang config: cần isLoading/isError/refetch). */
export function useRbacMatrixQuery() {
  return useQuery({
    queryKey: RBAC_MATRIX_KEY,
    queryFn: fetchRbacMatrix,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Mutation lưu matrix (SUPER_ADMIN). Thành công → cập nhật cache để nav đổi ngay. */
export function useSaveRbacMatrix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveRbacMatrix,
    onSuccess: (next) => {
      qc.setQueryData(RBAC_MATRIX_KEY, next);
    },
  });
}

/** Helper hook: vai hiện tại có THẤY nav-key? (dùng khi cần điều kiện render ngoài Sidebar). */
export function useCanViewNav(navKey: string): boolean {
  const matrix = useRbacMatrix();
  const role = getCurrentAdmin()?.role as AdminRole | undefined;
  return canViewNav(matrix, role, navKey);
}

/** Helper hook: vai hiện tại có được THAO TÁC (enable nút) trên màn nav-key? */
export function useCanActNav(navKey: string): boolean {
  const matrix = useRbacMatrix();
  const role = getCurrentAdmin()?.role as AdminRole | undefined;
  return canActNav(matrix, role, navKey);
}
